import { SDF_DEFAULT_MARGIN_MM } from '../sdfConstants';
import {
    buildPlaneFrame,
    canonicalisePolygon,
    resampleClosedPolygonUniform,
} from './crossSectionGeometry';
import {
    liftFromPlane,
    PlaneFrame,
    planesAreCoincident,
    PointMm,
    projectToPlane,
    toPointMm,
    Vec2Mm,
} from '~/domain/geometry/patientGeometry';
import { CrossSectionError, CrossSectionInput } from './crossSectionTypes';
import { Grid } from '~/engine/data/common/Grid';
import { taubinSmoothClosed } from '~/engine/util/geometry/taubinSmoothing';
import { vec3 } from 'gl-matrix';

/** A vertex may overhang the image by this many voxels before it counts as outside. */
const IMAGE_BOUNDS_TOLERANCE_VOXELS = 0.5;

/** Arc-length spacing of the fit rings in mm, fine enough that a 7 mm semi-minor ellipse tip is not flagged as a 15° corner. */
export const FIT_RING_SPACING_MM = 1;

/** Taubin pass pairs on the fit rings: removes pixel tremor, rounding a clicked corner to about 1 mm. */
export const FIT_SMOOTHING_PASSES = 10;

/** One plane's drawn contours (patient and in-plane mm), whose region is their union; `fitPolygons2d` is only the fit's smoothed target, never the stored datum. */
export interface PreparedSection {
    readonly frame: PlaneFrame;
    readonly polygonsMm: readonly (readonly PointMm[])[];
    readonly polygons2d: readonly (readonly Vec2Mm[])[];
    readonly fitPolygons2d: readonly (readonly Vec2Mm[])[];
}

type DrawnSection = Omit<PreparedSection, 'fitPolygons2d'>;

/** Checks the drawn sections and merges those on one plane; @throws CrossSectionError `no-sections`, `too-few-vertices`, `outside-image` or `invalid-parameter`. */
export function validateSections(
    sections: readonly CrossSectionInput[],
    imageGrid: Grid
): PreparedSection[] {
    if (sections.length === 0) {
        throw new CrossSectionError(
            'no-sections',
            'no cross-sections were drawn'
        );
    }
    return mergeCoincident(
        sections.map((section, index) =>
            prepareSection(section, index, imageGrid)
        )
    ).map(({ frame, polygonsMm, polygons2d }) => ({
        frame,
        polygonsMm,
        polygons2d,
        fitPolygons2d: polygons2d.map((ring) => {
            const lifted = resampleClosedPolygonUniform(
                ring,
                FIT_RING_SPACING_MM
            ).map((q) => liftFromPlane(frame, q));
            return taubinSmoothClosed(lifted, FIT_SMOOTHING_PASSES).map((p) =>
                projectToPlane(frame, toPointMm(p))
            );
        }),
    }));
}

function prepareSection(
    section: CrossSectionInput,
    index: number,
    imageGrid: Grid
): DrawnSection {
    const frame = buildPlaneFrame(section.plane);
    if (section.polygonsMm.length === 0) {
        throw new CrossSectionError(
            'too-few-vertices',
            `cross-section ${index} has no contours`
        );
    }
    const sizeMm = imageGrid.getSizeMM();
    const res = imageGrid.getRes();
    const polygonsMm: PointMm[][] = [];
    const polygons2d = section.polygonsMm.map((polygon, polygonIndex) => {
        const patientSpace = polygon.map((vertex) => {
            const pMm = toPointMm(vertex);
            if (!pMm.every((c) => Number.isFinite(c))) {
                throw new CrossSectionError(
                    'invalid-parameter',
                    `cross-section ${index}, contour ${polygonIndex} has a non-finite vertex`
                );
            }
            const imageMm = imageGrid.toImageMm(vertex);
            for (let axis = 0; axis < 3; axis++) {
                const slackMm = IMAGE_BOUNDS_TOLERANCE_VOXELS * res[axis];
                if (
                    imageMm[axis] < -slackMm ||
                    imageMm[axis] > sizeMm[axis] + slackMm
                ) {
                    throw new CrossSectionError(
                        'outside-image',
                        `cross-section ${index}, contour ${polygonIndex} extends ` +
                            `beyond the image volume on axis ${axis}`
                    );
                }
            }
            return pMm;
        });
        const ring = canonicalisePolygon(
            patientSpace.map((p) => projectToPlane(frame, p))
        );
        if (ring.length < 3) {
            throw new CrossSectionError(
                'too-few-vertices',
                `cross-section ${index}, contour ${polygonIndex} has ${ring.length} ` +
                    'distinct vertices; a closed contour needs at least 3'
            );
        }
        polygonsMm.push(patientSpace);
        return ring;
    });
    return { frame, polygonsMm, polygons2d };
}

function mergeCoincident(prepared: readonly DrawnSection[]): DrawnSection[] {
    const merged: DrawnSection[] = [];
    for (const section of prepared) {
        const at = merged.findIndex((candidate) =>
            planesAreCoincident(candidate.frame, section.frame)
        );
        if (at < 0) {
            merged.push(section);
            continue;
        }
        const { frame, polygonsMm, polygons2d } = merged[at];
        merged[at] = {
            frame,
            polygonsMm: [...polygonsMm, ...section.polygonsMm],
            polygons2d: [
                ...polygons2d,
                ...section.polygonsMm.map((polygon) =>
                    canonicalisePolygon(
                        polygon.map((p) => projectToPlane(frame, p))
                    )
                ),
            ],
        };
    }
    return merged;
}

type Triple = [number, number, number];
export interface SectionGridSpec {
    sizePx: vec3;
    originWorld: vec3;
    localMinMm: vec3;
}

/** The image-aligned sub-grid holding every section plus the SDF margin, clamped to the image. */
export function computeSectionGridSpec(
    sections: readonly PreparedSection[],
    imageGrid: Grid
): SectionGridSpec {
    const marginMm = SDF_DEFAULT_MARGIN_MM;
    const minMm: Triple = [Infinity, Infinity, Infinity];
    const maxMm: Triple = [-Infinity, -Infinity, -Infinity];
    for (const vertex of sections.flatMap((s) => s.polygonsMm.flat())) {
        const local = imageGrid.toImageMm(
            vec3.fromValues(vertex[0], vertex[1], vertex[2])
        );
        for (let axis = 0; axis < 3; axis++) {
            minMm[axis] = Math.min(minMm[axis], local[axis]);
            maxMm[axis] = Math.max(maxMm[axis], local[axis]);
        }
    }
    const snappedMin = imageGrid.floorMmToVoxelBoundary(
        vec3.sub(vec3.create(), minMm, [marginMm, marginMm, marginMm])
    );
    const snappedMax = imageGrid.ceilMmToVoxelBoundary(
        vec3.add(vec3.create(), maxMm, [marginMm, marginMm, marginMm])
    );
    const imageSizeMm = imageGrid.getSizeMM();
    const res = imageGrid.getRes();
    const clampedMin = vec3.create();
    const sizePx = vec3.create();
    for (let axis = 0; axis < 3; axis++) {
        clampedMin[axis] = Math.max(0, snappedMin[axis]);
        const clampedMax = Math.fround(
            Math.min(imageSizeMm[axis], snappedMax[axis])
        );
        if (clampedMin[axis] >= clampedMax) {
            throw new CrossSectionError(
                'outside-image',
                `The reconstruction extent along the image ${'XYZ'[axis]} ` +
                    'axis lies entirely outside the image volume'
            );
        }
        sizePx[axis] = Math.max(
            1,
            Math.round((clampedMax - clampedMin[axis]) / res[axis])
        );
    }
    return {
        sizePx,
        originWorld: imageGrid.fromImageMm(clampedMin),
        localMinMm: clampedMin,
    };
}

/** Patient-space position of the sub-grid's voxel centres: `origin + i·stepX + j·stepY + k·stepZ`. */
export function gridSampling(spec: SectionGridSpec, imageGrid: Grid) {
    const res = imageGrid.getRes();
    const at = (dx: number, dy: number, dz: number): vec3 =>
        imageGrid.fromImageMm(
            vec3.fromValues(
                spec.localMinMm[0] + dx,
                spec.localMinMm[1] + dy,
                spec.localMinMm[2] + dz
            )
        );
    const base = at(0, 0, 0);
    const step = (p: vec3): Triple => [
        p[0] - base[0],
        p[1] - base[1],
        p[2] - base[2],
    ];
    const stepX = step(at(res[0], 0, 0));
    const stepY = step(at(0, res[1], 0));
    const stepZ = step(at(0, 0, res[2]));
    const origin = [0, 1, 2].map(
        (a) => base[a] + 0.5 * (stepX[a] + stepY[a] + stepZ[a])
    ) as Triple;
    return {
        origin,
        stepX,
        stepY,
        stepZ,
        positionAt: (i: number, j: number, k: number): PointMm => [
            origin[0] + i * stepX[0] + j * stepY[0] + k * stepZ[0],
            origin[1] + i * stepX[1] + j * stepY[1] + k * stepZ[1],
            origin[2] + i * stepX[2] + j * stepY[2] + k * stepZ[2],
        ],
    };
}
